import { INPUT_METHOD } from '@atlaskit/editor-common/analytics';
import { TextSelection } from '@atlaskit/editor-prosemirror/state';
import { fg } from '@atlaskit/platform-feature-flags';
import type { EditorInsertContentCommandProperties } from '@atlassian/rovo-content-bridge-api-commands/commands/editor-insert-content-command';

import { CommandHandler, type PassThroughCommandProps } from './command-handler';

/**
 * Handles editor insert-content requests by delegating to the host-provided
 * `onInsertContent` callback, which wires the actual insert/replace logic into the editor.
 */
export class InsertContentCommandHandler extends CommandHandler<
	EditorInsertContentCommandProperties,
	void
> {
	constructor(props: PassThroughCommandProps) {
		super(props);
	}

	execute(payload: EditorInsertContentCommandProperties): void {
		const { editorAPI, onInsertContent, onUploadAndInsertMedia } = this.props;
		if (!editorAPI) {
			return;
		}

		// Capture the saved selection before entering the transaction
		// callback — so it survives any state changes triggered by closing the
		// ephemeral preview (which clears savedSelection via stopRemixThinking).
		const savedSelection =
			payload.useStoredSelection &&
			(payload.placement === 'after-block' ||
				(payload.placement === 'replace-selection' && fg('platform_editor_ai_remix_toolbar')))
				? editorAPI?.malleableUi?.sharedState?.currentState()?.savedSelection
				: undefined;

		editorAPI.core.actions.execute(({ tr }) => {
			let start = tr.selection.from,
				end = tr.selection.to;

			// Page-level remix: append content at the very end of the document,
			// regardless of the current cursor position.
			if (payload.placement === 'end-of-doc') {
				start = end = tr.doc.content.size - 1;
			} else if (
				payload.placement === 'after-block' ||
				(payload.placement === 'replace-selection' && fg('platform_editor_ai_remix_toolbar'))
			) {
				if (savedSelection) {
					const { from, to, anchorCellPos, headCellPos } = savedSelection;
					start = Math.min(
						from,
						anchorCellPos ?? Number.MAX_VALUE,
						headCellPos ?? Number.MAX_VALUE,
					);
					end = Math.max(to, anchorCellPos ?? 0, headCellPos ?? 0);
				}

				if (payload.placement === 'after-block') {
					const sharedDepth = tr.doc.resolve(start).sharedDepth(end);
					if (sharedDepth > 0) {
						start = end = tr.doc.resolve(end).after(1);
					}
				}
			}

			// upload-and-insert-media is async and handled separately via the React-layer callback
			// we are only setting the selection here.
			if (payload.mode === 'upload-and-insert-media' && onUploadAndInsertMedia) {
				const positions: [number, number] =
					payload.placement === 'replace-selection' ? [start, end] : [end, end];
				onUploadAndInsertMedia(payload.data.url, undefined, payload.data.presentation, {
					conversationChannelId: payload.conversationChannelId,
					isAIGenerated: payload.isAIGenerated,
					inputMethod:
						payload.interactionSource === 'remixModalFromMediaInserts'
							? INPUT_METHOD.REMIX_FROM_MEDIA_INSERTS
							: INPUT_METHOD.REMIX,
					aspectRatio: payload.aspectRatio,
					positions: fg('platform_editor_ai_remix_toolbar') ? positions : undefined,
				}).then((result) => {
					if (!fg('platform_editor_ai_remix_toolbar')) {
						return;
					}
					if (!result || !payload.conversationChannelId) {
						return;
					}
					const { fieldId, type } = result;
					if (type === 'create') {
						editorAPI.core.actions.execute(
							editorAPI.malleableUi?.commands.updateRemixImageHistoryFieldId({
								conversationId: payload.conversationChannelId,
								fieldId: fieldId,
							}),
						);
					}
				});
				const selection = TextSelection.create(tr.doc, end);
				return selection ? tr.setSelection(selection) : tr;
			}

			return (
				editorAPI.aiExperience?.commands.insertContent(payload, [start, end])?.({ tr }) ?? null
			);
		});

		onInsertContent?.(payload);
	}
}
